import { handleUploadFileUtils } from "api/fetchers";
import {
  SOURCE_OF_FUNDS_DOC_TYPE_OPTIONS_BY_SOURCE,
  SOURCE_OF_FUNDS_OPTIONS,
} from "./constants";
import {
  assignTargetIdentifier,
  getOptionLabel,
  getRequirementTargetId,
  isRejectedDirectorRequirement,
  isRejectedUboRequirement,
  normalizeKey,
  normalizeUploadResponse,
} from "./normalizationUtils";

export const buildAddressPayload = (value = {}) => ({
  houseNumber: String(value.houseNumber || "").trim(),
  street: String(value.street || "").trim(),
  city: String(value.city || "").trim(),
  state: String(value.state || "").trim(),
  zipCode: String(value.zipCode || "").trim(),
  subdivision: String(value.subdivision || "").trim(),
});

export const uploadFile = async (fileState) => {
  const file = fileState?.file;
  if (!file) return null;

  const raw = await handleUploadFileUtils({ selectedFile: file, name: file.name });
  const uploaded = normalizeUploadResponse(raw, file.name);
  if (!uploaded.url) throw new Error(raw?.message || "No upload URL returned.");
  return uploaded;
};

export const buildDocumentPayload = async (requirement, values, files, fileId = requirement.id) => {
  const currentValue = values[requirement.id] || {};
  const currentObj = typeof currentValue === "object" && currentValue !== null ? currentValue : {};
  const reqDocObj =
    typeof requirement?.document === "object" && requirement?.document !== null
      ? requirement.document
      : {};

  const {
    url: reqUrl,
    key: reqKey,
    name: _reqName,
    type: _reqType,
    status: _reqStatus,
    ...otherReqFields
  } = reqDocObj;

  const {
    url: curUrl,
    key: curKey,
    name: _curName,
    type: _curType,
    status: _curStatus,
    ...otherCurFields
  } = currentObj;

  let url = curUrl || reqUrl || (typeof currentValue === "string" ? currentValue : "");
  let key = curKey || reqKey || currentObj.name || "";

  const fileState = files[fileId] || files[requirement.id];
  if (fileState?.file && fileState.file.size > 0) {
    const uploaded = await uploadFile(fileState);
    if (uploaded?.url) {
      url = uploaded.url;
      key = uploaded.key || uploaded.name || key;
    }
  } else if (!url && fileState?.file) {
    const uploaded = await uploadFile(fileState);
    if (uploaded?.url) {
      url = uploaded.url;
      key = uploaded.key || uploaded.name || key;
    }
  }

  if (!key) {
    key = requirement.label || requirement.key || "";
  }

  return {
    url: url || "",
    key: key || "",
    ...otherReqFields,
    ...otherCurFields,
  };
};

const resolveFieldValue = async ({
  requirement,
  value,
  files,
  phoneCountryCode,
  getPhoneDialCode,
  kybCountriesData,
}) => {
  const type = normalizeKey(requirement?.type || "");
  const isCustomKey = String(requirement?.key || "").toLowerCase().startsWith("custom");

  if (type === "document" || type === "upload") {
    const docPayload = await buildDocumentPayload(requirement, { [requirement.id]: value }, files);
    return { document: docPayload };
  }

  if (type === "address") {
    const address = buildAddressPayload(value);
    return isCustomKey ? { value: address } : { address };
  }

  if (type === "country" || requirement.key?.includes("country")) {
    let countryObj = null;
    if (typeof value === "object" && value !== null) {
      countryObj = {
        name: value.name || "",
        code: value.code || "",
        continent: value.continent || "",
        flag: value.flag || "",
      };
    } else if (value) {
      const rawCountry = (kybCountriesData?.countries || []).find(
        (c) => c.name?.toLowerCase() === String(value).toLowerCase()
      );
      countryObj = {
        name: String(value),
        code: rawCountry?.code || "",
        continent: rawCountry?.continent || "",
        flag: rawCountry?.flag || "",
      };
    }
    return isCustomKey ? { value: countryObj || "" } : countryObj ? { value: countryObj } : null;
  }

  if (type === "business_type" || requirement.key === "business_type" || requirement.subtype === "BUSINESS_DETAILS") {
    const rawVal = Array.isArray(value) ? value[0] : value;
    const strVal = String(rawVal || "").trim();
    return isCustomKey ? { value: strVal } : { value: strVal };
  }

  if (
    type === "nature_of_business" ||
    requirement.key === "nature_of_business" ||
    requirement.key?.includes("nature_of_business")
  ) {
    const rawVal = Array.isArray(value) ? value[0] : value;
    const strVal =
      typeof rawVal === "object" && rawVal !== null
        ? rawVal.code || rawVal.nature || rawVal.value || ""
        : String(rawVal || "").trim();
    return isCustomKey ? { value: strVal } : { value: strVal };
  }

  if (type === "web_presence") {
    if (value?.hasWebsite) {
      return {
        webPresence: {
          hasWebsite: true,
          businessWebsite: value.businessWebsite,
        },
      };
    }
    const uploaded =
      value?.document?.url || (await uploadFile(files[`${requirement.id}-web`]));
    return {
      webPresence: {
        hasWebsite: false,
        supportingDocument: {
          url: uploaded?.url || value?.document?.url || "",
          key: uploaded?.key || value?.document?.key || "",
        },
      },
    };
  }

  if (type === "source_of_fund" || type === "source_of_funds") {
    const uploaded =
      value?.document?.url
        ? value.document
        : (await uploadFile(files[`${requirement.id}-source`]));
    const docTypeOptions =
      SOURCE_OF_FUNDS_DOC_TYPE_OPTIONS_BY_SOURCE[value?.source] || [];

    return {
      sourceOfFund: {
        url: uploaded?.url || value?.document?.url || "",
        key: uploaded?.key || value?.document?.key || "",
        type:
          getOptionLabel(docTypeOptions, value?.documentType) ||
          value?.documentType ||
          value?.rawType ||
          "",
        name:
          getOptionLabel(SOURCE_OF_FUNDS_OPTIONS, value?.source) ||
          value?.source ||
          value?.rawName ||
          "",
      },
    };
  }

  if (type === "monthly_transaction") {
    const transactionObj = {
      currency: value?.currency,
      amount: String(value?.amount || "").replace(/,/g, ""),
    };
    return { value: transactionObj };
  }

  if (type === "select") {
    if (typeof value === "string") {
      const items = value.split(",").map((item) => item.trim()).filter(Boolean);
      return { value: items.length > 0 ? items : value };
    }
    return { value: value || [] };
  }

  if (type === "phone_number" || type === "phone") {
    const dialCode = getPhoneDialCode(phoneCountryCode);
    const rawPhone = String(value || "").trim();
    const textValue =
      dialCode && rawPhone && !rawPhone.startsWith("+")
        ? `${dialCode}${rawPhone}`
        : rawPhone;
    return isCustomKey ? { value: textValue } : { textValue };
  }

  if (isCustomKey) {
    return { value: value !== undefined && value !== null ? value : "" };
  }

  if (typeof value === "object" && value !== null) {
    return { value };
  }

  return { textValue: String(value || "").trim() };
};

const handleMoreDataForStakeholder = ({
  payload,
  listKey,
  rawMoreData,
  requirement,
  isDirector,
}) => {
  if (!payload[listKey]) payload[listKey] = [];

  if (rawMoreData && typeof rawMoreData === "object" && Object.keys(rawMoreData).length > 0) {
    const moreDataObj = { ...rawMoreData };
    const targetId =
      getRequirementTargetId(requirement, isDirector) ||
      moreDataObj.targetId ||
      moreDataObj.target_id ||
      moreDataObj.targetID ||
      moreDataObj.id ||
      moreDataObj.targetEmail ||
      moreDataObj.email;
    if (targetId) {
      assignTargetIdentifier(moreDataObj, targetId);
    }
    if (!payload[listKey].some((item) => item.targetId === moreDataObj.targetId)) {
      payload[listKey].push(moreDataObj);
    }
  }
};

const sanitizeStakeholderList = (list) =>
  list
    .map((item) => {
      if (!item || typeof item !== "object") return item;
      const raw =
        item.targetId ||
        item.target_id ||
        item.targetID ||
        item.targetEmail ||
        item.target_email ||
        item.email;
      if (raw) assignTargetIdentifier(item, raw);
      return item;
    })
    .filter((item) => {
      if (!item || typeof item !== "object") return false;
      const validKeys = Object.keys(item).filter((k) => k !== "directors" && k !== "ubos");
      return validKeys.some((k) => Boolean(item[k]));
    });

export const buildPayload = async ({
  requirements,
  values,
  files,
  moreData,
  phoneCountryCode,
  phoneCountryOptions,
  getPhoneDialCode,
  businessCountry,
  kybCountriesData,
}) => {
  const payload = {};
  const hasRejectedDirector = requirements.some(isRejectedDirectorRequirement);
  const hasRejectedUbo = requirements.some(isRejectedUboRequirement);

  for (const requirement of requirements) {
    const value = values[requirement.id];
    const type = normalizeKey(requirement?.type || "");
    const subtypeRaw = requirement.subtype || requirement.subType || requirement.sub_type || "";
    const subtype = normalizeKey(subtypeRaw);
    const isDirector = type === "director" || subtype === "director" || subtype === "directors";
    const isUbo = type === "ubo" || subtype === "ubo" || subtype === "ubos";

    if (type === "director" || type === "ubo") {
      const listKey = type === "director" ? "directors" : "ubos";
      const shouldInclude = (type === "director" && hasRejectedDirector) || (type === "ubo" && hasRejectedUbo);
      if (shouldInclude) {
        handleMoreDataForStakeholder({
          payload,
          listKey,
          rawMoreData: type === "director" ? moreData?.director : moreData?.ubo,
          requirement,
          isDirector: type === "director",
        });
      }
      continue;
    }

    if (isDirector || isUbo) {
      const listKey = isDirector ? "directors" : "ubos";
      const shouldInclude = (isDirector && hasRejectedDirector) || (isUbo && hasRejectedUbo);
      if (shouldInclude) {
        handleMoreDataForStakeholder({
          payload,
          listKey,
          rawMoreData: isDirector ? moreData?.director : moreData?.ubo,
          requirement,
          isDirector,
        });
      }

      const rawTarget =
        requirement.targetId ||
        requirement.target_id ||
        requirement.targetID ||
        getRequirementTargetId(requirement, isDirector) ||
        requirement.targetEmail ||
        requirement.target_email;

      if (rawTarget) {
        let targetObj = payload[listKey].find(
          (item) =>
            item.targetId === rawTarget ||
            item.target_id === rawTarget ||
            item.targetID === rawTarget ||
            item.targetEmail === rawTarget ||
            item.target_email === rawTarget ||
            item.email === rawTarget
        );

        if (!targetObj) {
          targetObj = {};
          assignTargetIdentifier(targetObj, rawTarget);
          payload[listKey].push(targetObj);
        } else {
          assignTargetIdentifier(targetObj, rawTarget);
        }

        const isCustomKey = String(requirement?.key || "").toLowerCase().startsWith("custom");
        const resolved = await resolveFieldValue({
          requirement,
          value,
          files,
          phoneCountryCode,
          getPhoneDialCode,
          kybCountriesData,
        });

        if (isCustomKey) {
          targetObj[requirement.key] = resolved;
        } else if (resolved?.webPresence) {
          targetObj[requirement.key] = resolved.webPresence;
        } else if (resolved?.sourceOfFund) {
          targetObj[requirement.key] = resolved.sourceOfFund;
        } else if (resolved?.address) {
          targetObj[requirement.key] = resolved.address;
        } else if (resolved?.document) {
          targetObj[requirement.key] = resolved.document?.url || resolved.document;
        } else if (resolved?.textValue !== undefined) {
          targetObj[requirement.key] = resolved.textValue;
        } else if (resolved?.value !== undefined) {
          targetObj[requirement.key] = resolved.value;
        } else {
          targetObj[requirement.key] = resolved;
        }
      }
      continue;
    }

    const resolved = await resolveFieldValue({
      requirement,
      value,
      files,
      phoneCountryCode,
      getPhoneDialCode,
      kybCountriesData,
    });

    if (resolved !== null && resolved !== undefined) {
      if (requirement.type === "source_of_fund" || requirement.type === "source_of_funds") {
        payload["source_of_fund"] = resolved;
        if (requirement.key && requirement.key !== "source_of_fund") {
          payload[requirement.key] = resolved;
        }
      } else {
        payload[requirement.key] = resolved;
      }
    }

    if (requirement.type === "phone_number" || requirement.type === "phone") {
      const selectedCountry =
        phoneCountryOptions.find((option) => option.code === phoneCountryCode) ||
        phoneCountryOptions[0] ||
        (businessCountry
          ? {
              name: businessCountry.name || "",
              code: businessCountry.code || "",
              flag: businessCountry.flag || "",
              continent: businessCountry.continent || "",
            }
          : null);

      if (selectedCountry) {
        payload["country"] = {
          value: {
            name: selectedCountry.name || "",
            code: selectedCountry.code || "",
            flag: selectedCountry.flag || "",
            continent: selectedCountry.continent || "",
          },
        };
      }
    }
  }

  if (payload.directors) {
    payload.directors = sanitizeStakeholderList(payload.directors);
    if (payload.directors.length === 0) delete payload.directors;
  }

  if (payload.ubos) {
    payload.ubos = sanitizeStakeholderList(payload.ubos);
    if (payload.ubos.length === 0) delete payload.ubos;
  }

  return payload;
};
